import {
  LiveKitRoom,
  RoomAudioRenderer,
  useConnectionState,
  useLocalParticipant,
  useMediaDeviceSelect,
  useParticipants,
} from "@livekit/components-react";
import { ConnectionState, DisconnectReason } from "livekit-client";
import { useCallback, useEffect, useRef, useState } from "react";

import { Avatar, Tooltip } from "@web-app/components";
import config from "@web-app/config";

import { CopyLinkIcon } from "./icons/CopyLinkIcon";
import CubeIcon from "./icons/CubeIcon";
import DeclineCallIcon from "./icons/DeclineCallIcon";
import { MicDisabledIcon } from "./icons/MicDisabledIcon";
import { MicIcon } from "./icons/MicIcon";

interface VoiceCallPopupProps {
  headerImage?: string;
  headerName: string;
  orderDisplayId?: string;
  token: string;
}

function CallDuration({ startTime }: { startTime: number }) {
  const [duration, setDuration] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setDuration(Math.floor((Date.now() - startTime) / 1000));
    }, 1000);

    return () => clearInterval(interval);
  }, [startTime]);

  const minutes = Math.floor(duration / 60);
  const seconds = duration % 60;

  return (
    <span className="inline-flex items-center px-3 py-1 rounded-full bg-white border border-gray-200 text-sm text-gray-600">
      {String(minutes).padStart(2, "0")}:{String(seconds).padStart(2, "0")}
    </span>
  );
}

function ParticipantsList() {
  const participants = useParticipants();
  const { localParticipant } = useLocalParticipant();

  const parseMetadata = (metadata?: string) => {
    if (!metadata) return { image: "", phone: "" };
    try {
      return JSON.parse(metadata) as { image: string; phone: string };
    } catch {
      return { image: "", phone: "" };
    }
  };

  return (
    <div className="flex flex-row items-center gap-8">
      {participants.map((participant) => {
        const isLocal = participant.identity === localParticipant.identity;
        const metadata = parseMetadata(participant.metadata);
        const displayName = participant.name || participant.identity;
        const isSpeaking = participant.isSpeaking;

        return (
          <div
            key={participant.identity}
            className="flex flex-col items-center"
          >
            <div className="relative mb-3">
              <div className="rounded-2xl bg-gray-200 p-4">
                <div
                  className={`rounded-full p-1 transition-all duration-150 ${
                    isSpeaking ? "ring-4 ring-green-500" : ""
                  }`}
                >
                  <Avatar image={metadata.image} name={displayName} size={28} />
                </div>
              </div>
              <div
                className={`absolute -bottom-1 -right-1 rounded-full p-1.5 ${
                  participant.isMicrophoneEnabled
                    ? "bg-green-500"
                    : "bg-red-500"
                }`}
              >
                {participant.isMicrophoneEnabled ? (
                  <MicIcon className="w-4 h-4 text-white" />
                ) : (
                  <MicDisabledIcon className="w-4 h-4 text-white" />
                )}
              </div>
            </div>
            <p className="text-xl font-semibold text-gray-900">
              {displayName}
              {isLocal && " (You)"}
            </p>
          </div>
        );
      })}
    </div>
  );
}

const CALL_TIMEOUT_SECONDS = 60;
const SHOW_WARNING_AT_SECONDS = 10;

function getDisconnectReasonLabel(reason?: DisconnectReason): string {
  if (reason === undefined) return "Unknown";
  switch (reason) {
    case DisconnectReason.CLIENT_INITIATED:
      return "You ended the call";
    case DisconnectReason.DUPLICATE_IDENTITY:
      return "Duplicate identity (same user joined from another device)";
    case DisconnectReason.SERVER_SHUTDOWN:
      return "Server shutdown";
    case DisconnectReason.PARTICIPANT_REMOVED:
      return "Removed from call";
    case DisconnectReason.ROOM_DELETED:
      return "Room was deleted";
    case DisconnectReason.STATE_MISMATCH:
      return "Connection state mismatch";
    case DisconnectReason.JOIN_FAILURE:
      return "Failed to join call";
    case DisconnectReason.SIGNAL_CLOSE:
      return "Signal connection closed";
    default:
      return `Unknown (code: ${reason})`;
  }
}

interface ConnectionLog {
  details?: string;
  event: string;
  timestamp: Date;
}

function ConnectionStateTracker({
  onDisconnect,
  onLog,
}: {
  onDisconnect: (reason?: DisconnectReason) => void;
  onLog: (log: ConnectionLog) => void;
}) {
  const connectionState = useConnectionState();
  const prevState = useRef<ConnectionState | null>(null);
  const hasConnected = useRef(false);

  useEffect(() => {
    if (prevState.current !== connectionState) {
      onLog({
        event: `Connection state: ${connectionState}`,
        timestamp: new Date(),
      });
      prevState.current = connectionState;
    }

    if (connectionState === ConnectionState.Connected) {
      hasConnected.current = true;
    }

    if (
      connectionState === ConnectionState.Disconnected &&
      hasConnected.current
    ) {
      onDisconnect(undefined);
    }
  }, [connectionState, onDisconnect, onLog]);

  if (connectionState === ConnectionState.Reconnecting) {
    return (
      <div className="absolute top-20 left-0 right-0 z-10 flex justify-center px-4">
        <div className="bg-yellow-100 border border-yellow-300 text-yellow-800 px-4 py-2 rounded-lg shadow-md text-sm">
          Reconnecting...
        </div>
      </div>
    );
  }

  return null;
}

function CallTimeoutHandler({ onTimeout }: { onTimeout: () => void }) {
  const participants = useParticipants();
  const [secondsRemaining, setSecondsRemaining] = useState<number | null>(null);

  useEffect(() => {
    if (participants.length > 1) {
      setSecondsRemaining(null);
      return;
    }

    setSecondsRemaining(CALL_TIMEOUT_SECONDS);

    const interval = setInterval(() => {
      setSecondsRemaining((prev) => {
        if (prev === null || prev <= 1) {
          clearInterval(interval);
          onTimeout();
          return null;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [participants.length, onTimeout]);

  if (secondsRemaining === null || secondsRemaining > SHOW_WARNING_AT_SECONDS) {
    return null;
  }

  return (
    <div className="absolute top-20 left-0 right-0 z-10 flex justify-center px-4">
      <div className="bg-amber-100 border border-amber-300 text-amber-800 px-4 py-2 rounded-lg shadow-md text-sm">
        No one has joined. Ending call in {secondsRemaining}s...
      </div>
    </div>
  );
}

function Controls({ onDisconnect }: { onDisconnect: () => void }) {
  const { localParticipant } = useLocalParticipant();
  const [isMuted, setIsMuted] = useState(false);
  const [copied, setCopied] = useState(false);
  const [showDeviceSelector, setShowDeviceSelector] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const {
    devices: audioInputs,
    activeDeviceId,
    setActiveMediaDevice,
  } = useMediaDeviceSelect({ kind: "audioinput" });

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setShowDeviceSelector(false);
      }
    };

    if (showDeviceSelector) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showDeviceSelector]);

  const toggleMic = async () => {
    if (localParticipant) {
      await localParticipant.setMicrophoneEnabled(isMuted);
      setIsMuted(!isMuted);
    }
  };

  const handleDeviceChange = async (deviceId: string) => {
    await setActiveMediaDevice(deviceId);
    setShowDeviceSelector(false);
  };

  const copyLink = async () => {
    const conversationId = window.location.pathname.split("/").pop() || "";
    const shareUrl = `${window.location.origin}/call/${conversationId}`;
    await navigator.clipboard.writeText(shareUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="flex items-center justify-center gap-4">
      <Tooltip
        text={
          <div>
            <p className="font-semibold text-gray-900">
              Tap to copy meeting link
            </p>
            <p className="text-slate-500">
              Only users with access to this workspace can open this link
            </p>
          </div>
        }
        position="top"
        tooltipClassName="!bg-slate-100 !text-gray-900 shadow-lg border border-gray-200 [&>div]:!bg-slate-100"
        showIcon={false}
      >
        <button
          onClick={copyLink}
          className={`w-12 h-12 rounded-full flex items-center justify-center transition-colors ${
            copied
              ? "bg-green-100 hover:bg-green-200"
              : "bg-gray-100 hover:bg-gray-200"
          }`}
        >
          <CopyLinkIcon
            className={`w-5 h-5 ${copied ? "text-green-700" : "text-gray-700"}`}
          />
        </button>
      </Tooltip>
      <div className="relative" ref={dropdownRef}>
        {showDeviceSelector && audioInputs.length > 0 && (
          <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 bg-white border border-gray-200 rounded-lg shadow-lg p-2 min-w-[250px] z-10">
            <p className="text-xs font-semibold text-gray-500 px-2 py-1">
              Select Microphone
            </p>
            {audioInputs.map((device) => (
              <button
                key={device.deviceId}
                onClick={() => handleDeviceChange(device.deviceId)}
                className={`w-full text-left px-3 py-2 rounded-md text-sm transition-colors ${
                  device.deviceId === activeDeviceId
                    ? "bg-blue-50 text-blue-700 font-medium"
                    : "text-gray-700 hover:bg-gray-50"
                }`}
              >
                <div className="flex items-center justify-between">
                  <span className="truncate">{device.label}</span>
                  {device.deviceId === activeDeviceId && (
                    <svg
                      className="w-4 h-4 text-blue-600 flex-shrink-0 ml-2"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                  )}
                </div>
              </button>
            ))}
          </div>
        )}
        <button
          onClick={toggleMic}
          className={`w-12 h-12 rounded-full flex items-center justify-center transition-colors ${
            isMuted
              ? "bg-gray-200 hover:bg-gray-300"
              : "bg-gray-100 hover:bg-gray-200"
          }`}
          title={isMuted ? "Unmute" : "Mute"}
        >
          {isMuted ? (
            <MicDisabledIcon className="w-5 h-5 text-gray-700" />
          ) : (
            <MicIcon className="w-5 h-5 text-gray-700" />
          )}
        </button>
        <button
          onClick={() => setShowDeviceSelector(!showDeviceSelector)}
          className="absolute -top-1 -right-1 w-6 h-6 bg-white border border-gray-300 rounded-full flex items-center justify-center hover:bg-gray-50 shadow-sm"
          title="Select microphone"
        >
          <svg
            className="w-3 h-3 text-gray-600"
            fill="currentColor"
            viewBox="0 0 16 16"
          >
            <circle cx="3" cy="8" r="1.5" />
            <circle cx="8" cy="8" r="1.5" />
            <circle cx="13" cy="8" r="1.5" />
          </svg>
        </button>
      </div>
      <button
        onClick={onDisconnect}
        className="transition-transform hover:scale-105 active:scale-95"
        title="End Call"
      >
        <DeclineCallIcon className="w-12 h-12" />
      </button>
    </div>
  );
}

function CallContent({
  onDisconnect,
  headerName,
  headerImage,
  orderDisplayId,
  startTime,
}: {
  headerImage?: string;
  headerName: string;
  onDisconnect: () => void;
  orderDisplayId?: string;
  startTime: number;
}) {
  return (
    <div className="h-full flex flex-col">
      <div className="flex items-center justify-between p-4 bg-white border-b border-gray-200">
        <div className="flex items-center gap-3">
          <Avatar image={headerImage} name={headerName} size={10} />
          <div>
            <p className="font-semibold text-gray-900">{headerName}</p>
            {orderDisplayId && (
              <p className="text-xs text-gray-500 flex items-center gap-1">
                <CubeIcon className="w-3.5 h-3.5" />
                {orderDisplayId}
              </p>
            )}
          </div>
        </div>
        <button
          onClick={onDisconnect}
          className="p-2 hover:bg-gray-100 rounded-md transition-colors"
        >
          <svg
            className="w-5 h-5 text-gray-500"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fillRule="evenodd"
              d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
              clipRule="evenodd"
            />
          </svg>
        </button>
      </div>

      <div className="flex-1 flex flex-col items-center justify-center bg-gray-50 px-6 overflow-y-auto">
        <ParticipantsList />
        <div className="mt-4">
          <CallDuration startTime={startTime} />
        </div>
      </div>

      <div className="bg-gray-50 pb-6 px-6">
        <Controls onDisconnect={onDisconnect} />
      </div>

      <RoomAudioRenderer />
    </div>
  );
}

function CallEndedScreen({
  reason,
  logs,
  onRejoin,
  onClose,
  userInitiated,
}: {
  logs: ConnectionLog[];
  onClose: () => void;
  onRejoin: () => void;
  reason?: DisconnectReason;
  userInitiated: boolean;
}) {
  const reasonLabel = getDisconnectReasonLabel(reason);
  const [showLogs, setShowLogs] = useState(false);

  const getMessage = () => {
    if (userInitiated) return "You left the call";
    if (!reason) return "Call ended";
    return "Call disconnected";
  };

  return (
    <div className="h-screen bg-gray-50 flex flex-col items-center justify-center p-6">
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
        <h2 className="text-2xl font-medium text-gray-900 mb-2">
          {getMessage()}
        </h2>

        {!userInitiated && reason && (
          <p className="text-sm text-gray-500 mb-6">{reasonLabel}</p>
        )}

        <div className="flex gap-3 justify-center mt-6">
          <button
            onClick={onRejoin}
            className="py-2 px-6 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Rejoin
          </button>
          <button
            onClick={onClose}
            className="py-2 px-6 bg-gray-800 text-white rounded-lg hover:bg-gray-700 transition-colors"
          >
            Return to home page
          </button>
        </div>

        {!userInitiated && logs.length > 0 && (
          <div className="mt-6">
            <button
              onClick={() => setShowLogs(!showLogs)}
              className="text-xs text-gray-400 hover:text-gray-600 flex items-center gap-1 mx-auto"
            >
              {showLogs ? "Hide" : "Show"} details
              <svg
                className={`w-3 h-3 transition-transform ${showLogs ? "rotate-180" : ""}`}
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </button>
            {showLogs && (
              <div className="bg-gray-50 border border-gray-200 rounded-md p-3 mt-2 max-h-32 overflow-y-auto text-left">
                {logs.map((log, index) => (
                  <div key={index} className="text-xs text-gray-600 mb-1">
                    <span className="text-gray-400">
                      {log.timestamp.toLocaleTimeString()}
                    </span>{" "}
                    {log.event}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default function VoiceCallPopup({
  token,
  headerName,
  headerImage,
  orderDisplayId,
}: VoiceCallPopupProps) {
  const [startTime] = useState(Date.now());
  const [disconnected, setDisconnected] = useState(false);
  const [disconnectReason, setDisconnectReason] = useState<
    DisconnectReason | undefined
  >();
  const [connectionLogs, setConnectionLogs] = useState<ConnectionLog[]>([
    { event: "Call page opened", timestamp: new Date() },
  ]);

  const addLog = useCallback((log: ConnectionLog) => {
    setConnectionLogs((prev) => [...prev, log]);
  }, []);

  const handleDisconnect = useCallback(
    (reason?: DisconnectReason) => {
      addLog({
        event: `Disconnected: ${getDisconnectReasonLabel(reason)}`,
        timestamp: new Date(),
      });
      setDisconnectReason(reason);
      setDisconnected(true);
    },
    [addLog],
  );

  const handleUserEndCall = useCallback(() => {
    handleDisconnect(DisconnectReason.CLIENT_INITIATED);
  }, [handleDisconnect]);

  const handleClose = useCallback(() => {
    if (window.opener) {
      window.opener.focus();
      window.close();
    } else {
      window.location.href = "/";
    }
  }, []);

  const handleTimeout = useCallback(() => {
    addLog({ event: "Timeout: No participants joined", timestamp: new Date() });
    setDisconnected(true);
  }, [addLog]);

  const handleRejoin = useCallback(() => {
    setDisconnected(false);
    setDisconnectReason(undefined);
    setConnectionLogs([{ event: "Rejoining call...", timestamp: new Date() }]);
  }, []);

  if (disconnected) {
    return (
      <CallEndedScreen
        reason={disconnectReason}
        logs={connectionLogs}
        onRejoin={handleRejoin}
        onClose={handleClose}
        userInitiated={disconnectReason === DisconnectReason.CLIENT_INITIATED}
      />
    );
  }

  return (
    <div className="h-screen bg-gray-50 flex flex-col relative">
      <LiveKitRoom
        token={token}
        serverUrl={config.LIVEKIT_URL}
        audio={true}
        video={false}
        connect={true}
        onDisconnected={(reason) => handleDisconnect(reason)}
        onError={(error) => {
          addLog({
            event: `Error: ${error.message}`,
            timestamp: new Date(),
          });
        }}
        onMediaDeviceFailure={(failure) => {
          addLog({
            event: `Media device failure: ${failure || "Unknown"}`,
            timestamp: new Date(),
          });
        }}
        style={{ display: "flex", flexDirection: "column", height: "100%" }}
      >
        <ConnectionStateTracker
          onDisconnect={handleDisconnect}
          onLog={addLog}
        />
        <CallTimeoutHandler onTimeout={handleTimeout} />
        <CallContent
          onDisconnect={handleUserEndCall}
          headerName={headerName}
          headerImage={headerImage}
          orderDisplayId={orderDisplayId}
          startTime={startTime}
        />
      </LiveKitRoom>
    </div>
  );
}
